<template>
  <section class="locations-section" id="locations">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title">League Locations</h2>
        <p class="section-subtitle">Two premier venues with more locations coming soon</p>
      </div>
      
      <div class="locations-grid">
        <div class="location-card" v-for="location in locations" :key="location.name">
          <div class="location-header">
            <div class="location-info">
              <h3>
                <a v-if="!location.isComingSoon" :href="location.mapUrl" target="_blank" rel="noopener noreferrer" class="location-name-link">
                  {{ location.name }}
                </a>
                <span v-else class="location-name-coming-soon">
                  {{ location.name }}
                </span>
              </h3>
              <div class="location-day">
                <Icon :name="location.isComingSoon ? 'mdi:notification-clear-all' : 'mdi:calendar'" />
                <span>{{ location.day }}</span>
              </div>
            </div>
          </div>
          
          <div class="location-address">
            <Icon :name="location.isComingSoon ? 'mdi:map-marker-plus' : 'mdi:map-marker'" />
            <a v-if="!location.isComingSoon" :href="location.mapUrl" target="_blank" rel="noopener noreferrer" class="location-address-link">
              {{ location.address }}
            </a>
            <span v-else class="location-address-coming-soon">
              {{ location.address }}
            </span>
          </div>
          
          <div class="location-actions">
            <NuxtLink v-if="location.isComingSoon" to="/contact" class="view-map-link contact-link">
              <Icon name="mdi:email-outline" />
              <span>Get Notified</span>
            </NuxtLink>
            <a v-else :href="location.mapUrl" target="_blank" rel="noopener noreferrer" class="view-map-link">
              <Icon name="mdi:map-search" />
              <span>View on Map</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const locations = [
  {
    name: 'WiseGuys Billiards Sports Bar',
    day: 'Coming Soon',
    address: '5000 Western Center Blvd suite 100, Haltom City, TX 76137',
    mapUrl: 'https://www.google.com/maps/place/WiseGuys+Billiards+%26+Sports+Bar/@32.8595943,-97.2763383,19.07z/data=!4m6!3m5!1s0x864e7744e1265701:0x87282b1990b4d5dc!8m2!3d32.8602609!4d-97.277376!16s%2Fg%2F11y0c02y39?entry=ttu&g_ep=EgoyMDI1MTAxNC4wIKXMDSoASAFQAw%3D%3D'
  },
  {
    name: 'Side Pockets',
    day: 'Tuesdays',
    address: '301 N Elm St, Waxahachie, TX 75165',
    mapUrl: 'https://www.google.com/maps/place/Side+Pocket+Billiards/@32.3885445,-96.8513472,16z/data=!3m1!4b1!4m6!3m5!1s0x864eef2453ef5dd7:0xcfda4a99a0f62f3f!8m2!3d32.3885445!4d-96.8487723!16s%2Fg%2F11csq7450x?entry=ttu&g_ep=EgoyMDI1MDYxMC4xIKXMDSoASAFQAw%3D%3D'
  },
  {
    name: 'The Billiard Parlor',
    day: 'Wednesdays & Fridays (Ladies League)',
    address: '6531 Watauga Rd #110C, Watauga, TX 76148',
    mapUrl: 'https://www.google.com/maps/place/The+Billiard+Parlor/@32.8610899,-97.24466,17z/data=!3m1!4b1!4m6!3m5!1s0x864e79b34cfe25f5:0x5f44ac38879dc08f!8m2!3d32.8610899!4d-97.2420851!16s%2Fg%2F11vhdk5_hq?entry=ttu&g_ep=EgoyMDI1MDYxMC4xIKXMDSoASAFQAw%3D%3D'
  },
  {
    name: 'More Locations Coming Soon',
    day: 'Stay Tuned',
    address: 'Expanding across the Dallas-Fort Worth metroplex',
    mapUrl: '/contact',
    isComingSoon: true
  }
];
</script>

<style scoped>
.locations-section {
  padding: 80px 0;
  background: 
    linear-gradient(180deg, rgba(30, 58, 138, 0.85) 0%, rgba(59, 130, 246, 0.85) 100%),
    url('/images/billiards-1-1920w.webp');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-header {
  text-align: center;
  margin-bottom: 64px;
}

.section-title {
  color: var(--gold);
  font-size: 3rem;
  font-family: var(--font-heading);
  font-weight: 700;
  margin-bottom: 16px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  border-radius: 2px;
}

.section-subtitle {
  color: var(--cream);
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
}

.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 32px;
  margin-top: 48px;
}

.location-card {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(15px);
  border-radius: clamp(10px, 1.5vw, 16px);
  padding: clamp(14px, 2.5vw, 24px);
  border: 1px solid rgba(255, 184, 28, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Make the coming soon card span full width */
.location-card:has(.location-name-coming-soon) {
  grid-column: 1 / -1;
  padding: clamp(20px, 3vw, 30px);
}

/* Reduce coming soon card content size */
.location-card:has(.location-name-coming-soon) .location-header {
  margin-bottom: 0;
}

.location-card:has(.location-name-coming-soon) .location-info h3 {
  font-size: clamp(1rem, 2vw, 1.2rem);
  margin: 0;
}

.location-card:has(.location-name-coming-soon) .location-address {
  display: none;
}

.location-card:has(.location-name-coming-soon) .location-actions {
  display: none;
}

.location-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 184, 28, 0.05), transparent);
  transition: left 0.6s ease;
}

.location-card:hover {
  transform: translateY(-8px);
  border-color: var(--gold);
  box-shadow: 0 20px 40px rgba(255, 184, 28, 0.15), 0 0 30px rgba(255, 184, 28, 0.1);
}

.location-card:hover::before {
  left: 100%;
}

.location-header {
  margin-bottom: clamp(12px, 2vw, 16px);
}

.location-info {
  width: 100%;
}

.location-info h3 {
  margin: 0 0 clamp(6px, 1vw, 8px) 0;
  font-size: clamp(0.85rem, 2vw + 0.4rem, 1.4rem);
  font-family: var(--font-heading);
  font-weight: 600;
  line-height: 1.2;
}

.location-name-link {
  color: var(--gold);
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
}

.location-name-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--light-gold);
  transition: width 0.3s ease;
}

.location-name-link:hover {
  color: var(--light-gold);
}

.location-name-link:hover::after {
  width: 100%;
}

.location-name-coming-soon {
  color: var(--sky-blue);
  font-weight: 600;
}

.location-address-coming-soon {
  color: var(--cream);
  line-height: 1.5;
  opacity: 0.8;
  font-style: italic;
}

.contact-link {
  background: linear-gradient(135deg, var(--sky-blue) 0%, var(--light-blue) 100%) !important;
  color: var(--navy-blue) !important;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3) !important;
}

.contact-link:hover {
  background: linear-gradient(135deg, var(--light-blue) 0%, var(--sky-blue) 100%) !important;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4) !important;
}

.location-day {
  display: flex;
  align-items: center;
  gap: clamp(4px, 0.8vw, 6px);
  color: var(--light-gold);
  font-weight: 600;
  font-size: clamp(0.7rem, 1.3vw + 0.3rem, 0.95rem);
}

.location-address {
  display: flex;
  align-items: flex-start;
  gap: clamp(6px, 1vw, 10px);
  margin-bottom: clamp(12px, 2vw, 16px);
  padding: clamp(10px, 1.5vw, 14px);
  background: rgba(0, 0, 0, 0.2);
  border-radius: clamp(6px, 1vw, 10px);
  border-left: 3px solid var(--gold);
}

.location-address > svg {
  color: var(--gold);
  font-size: clamp(0.9rem, 1.5vw + 0.3rem, 1.2rem);
  margin-top: 2px;
  flex-shrink: 0;
}

.location-address-link {
  color: var(--cream);
  text-decoration: none;
  line-height: 1.5;
  transition: color 0.3s ease;
  font-size: clamp(0.75rem, 1.2vw + 0.4rem, 1rem);
}

.location-address-link:hover {
  color: var(--gold);
}

.location-actions {
  display: flex;
  justify-content: center;
}

.view-map-link {
  display: inline-flex;
  align-items: center;
  gap: clamp(4px, 0.8vw, 8px);
  padding: clamp(8px, 1.5vw, 12px) clamp(12px, 2vw, 20px);
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--navy-blue);
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: clamp(0.65rem, 1vw + 0.2rem, 0.85rem);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 20px rgba(255, 184, 28, 0.3);
  position: relative;
  z-index: 2;
  white-space: nowrap;
}

.view-map-link:hover {
  background: linear-gradient(135deg, var(--light-gold) 0%, var(--gold) 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 184, 28, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .locations-section {
    padding: 60px 0;
  }
  
  .section-container {
    padding: 0 16px;
  }
  
  .section-header {
    margin-bottom: 48px;
  }
  
  .section-title {
    font-size: 2.2rem;
  }
  
  .section-subtitle {
    font-size: 1.1rem;
  }
  
  .locations-grid {
    grid-template-columns: repeat(auto-fit, minmax(min(250px, 100%), 1fr));
    gap: 16px;
  }
  
  .location-card {
    padding: 20px;
  }
  
  .location-header {
    flex-direction: row; /* Keep horizontal on mobile to save space */
    text-align: left;
    gap: 12px;
  }
  
  .location-icon {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
  
  .location-info h3 {
    font-size: 1.3rem;
  }
  
  .location-address {
    padding: 12px;
    margin-bottom: 16px;
  }
  
  .view-map-link {
    padding: 12px 20px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .locations-section {
    padding: 40px 0;
  }
  
  .section-container {
    padding: 0 12px;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .locations-grid {
    grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr));
    gap: 12px;
  }
  
  .location-card {
    padding: 16px;
  }
  
  .location-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
  }
  
  .location-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
  
  .location-info h3 {
    font-size: 1.1rem;
  }
  
  .location-day {
    font-size: 0.9rem;
    justify-content: center;
  }
  
  .location-address {
    padding: 10px;
    margin-bottom: 12px;
    font-size: 0.85rem;
  }
  
  .view-map-link {
    padding: 10px 16px;
    font-size: 0.85rem;
    gap: 8px;
  }
  
  .location-card {
    padding: 20px;
  }
  
  .location-icon {
    width: 45px;
    height: 45px;
    font-size: 1.3rem;
  }
  
  .location-info h3 {
    font-size: 1.2rem;
  }
  
  .view-map-link {
    width: 100%;
    justify-content: center;
    padding: 16px 24px;
  }
}

/* Extra small devices */
@media (max-width: 360px) {
  .location-header {
    gap: 10px;
  }
  
  .location-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
  
  .location-info h3 {
    font-size: 1.1rem;
  }
}
</style>
